import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import transactionService from '../../services/transactionService';

const initialState = {
  // Project transactions (from external API)
  transactions: [],
  // Approved transactions (from local database)
  approvedTransactions: [],
  currentTransaction: null,
  stats: null,
  pagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  approvedPagination: {
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  },
  filters: {
    status: 'pending',
    search: '',
    startDate: '',
    endDate: ''
  },
  selectedTransactions: [],
  loading: false,
  bulkLoading: false,
  error: null
};

// Fetch transactions from external project API
export const fetchProjectTransactions = createAsyncThunk(
  'transactions/fetchProject',
  async ({ project, params }, thunkAPI) => {
    try {
      const response = await transactionService.getProjectTransactions(project, params);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch transactions';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Fetch approved transactions from local database
export const fetchApprovedTransactions = createAsyncThunk(
  'transactions/fetchApproved',
  async (params, thunkAPI) => {
    try {
      const response = await transactionService.getApprovedTransactions(params);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch approved transactions';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Approve single transaction
export const approveTransaction = createAsyncThunk(
  'transactions/approve',
  async ({ project, transactionData }, thunkAPI) => {
    try {
      const response = await transactionService.approveTransaction(project, transactionData);
      return { transactionId: transactionData.id, result: response.data };
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to approve transaction';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Bulk approve transactions
export const bulkApproveTransactions = createAsyncThunk(
  'transactions/bulkApprove',
  async ({ project, transactions }, thunkAPI) => {
    try {
      const response = await transactionService.bulkApproveTransactions(project, transactions);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to approve transactions';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Decline single transaction
export const declineTransaction = createAsyncThunk(
  'transactions/decline',
  async ({ project, transactionId, reason }, thunkAPI) => {
    try {
      await transactionService.declineTransaction(project, transactionId, reason);
      return transactionId;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to decline transaction';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Bulk decline transactions
export const bulkDeclineTransactions = createAsyncThunk(
  'transactions/bulkDecline',
  async ({ project, transactionIds, reason }, thunkAPI) => {
    try {
      const response = await transactionService.bulkDeclineTransactions(project, transactionIds, reason);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to decline transactions';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

// Fetch transaction stats
export const fetchTransactionStats = createAsyncThunk(
  'transactions/fetchStats',
  async (project, thunkAPI) => {
    try {
      const response = await transactionService.getTransactionStats(project);
      return response.data;
    } catch (error) {
      const message = error.response?.data?.message || 'Failed to fetch stats';
      return thunkAPI.rejectWithValue(message);
    }
  }
);

const transactionSlice = createSlice({
  name: 'transactions',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = initialState.filters;
    },
    setPage: (state, action) => {
      state.pagination.page = action.payload;
    },
    setApprovedPage: (state, action) => {
      state.approvedPagination.page = action.payload;
    },
    toggleTransactionSelection: (state, action) => {
      const transactionId = action.payload;
      const index = state.selectedTransactions.findIndex(id => id === transactionId);
      if (index === -1) {
        state.selectedTransactions.push(transactionId);
      } else {
        state.selectedTransactions.splice(index, 1);
      }
    },
    selectAllTransactions: (state) => {
      state.selectedTransactions = state.transactions.map(t => t.id);
    },
    clearSelectedTransactions: (state) => {
      state.selectedTransactions = [];
    },
    clearCurrentTransaction: (state) => {
      state.currentTransaction = null;
    },
    clearError: (state) => {
      state.error = null;
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch project transactions
      .addCase(fetchProjectTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchProjectTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.transactions = action.payload.data;
        state.pagination = action.payload.pagination;
        state.selectedTransactions = [];
      })
      .addCase(fetchProjectTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Fetch approved transactions
      .addCase(fetchApprovedTransactions.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchApprovedTransactions.fulfilled, (state, action) => {
        state.loading = false;
        state.approvedTransactions = action.payload.data;
        state.approvedPagination = action.payload.pagination;
      })
      .addCase(fetchApprovedTransactions.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Approve single transaction
      .addCase(approveTransaction.pending, (state) => {
        state.loading = true;
      })
      .addCase(approveTransaction.fulfilled, (state, action) => {
        state.loading = false;
        // Remove approved transaction from list
        state.transactions = state.transactions.filter(t => t.id !== action.payload.transactionId);
        state.selectedTransactions = state.selectedTransactions.filter(id => id !== action.payload.transactionId);
      })
      .addCase(approveTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk approve transactions
      .addCase(bulkApproveTransactions.pending, (state) => {
        state.bulkLoading = true;
      })
      .addCase(bulkApproveTransactions.fulfilled, (state, action) => {
        state.bulkLoading = false;
        // Remove approved transactions from list
        const approvedIds = action.payload.success.map(s => s.id);
        state.transactions = state.transactions.filter(t => !approvedIds.includes(t.id));
        state.selectedTransactions = [];
      })
      .addCase(bulkApproveTransactions.rejected, (state, action) => {
        state.bulkLoading = false;
        state.error = action.payload;
      })

      // Decline single transaction
      .addCase(declineTransaction.pending, (state) => {
        state.loading = true;
      })
      .addCase(declineTransaction.fulfilled, (state, action) => {
        state.loading = false;
        // Remove declined transaction from list
        state.transactions = state.transactions.filter(t => t.id !== action.payload);
        state.selectedTransactions = state.selectedTransactions.filter(id => id !== action.payload);
      })
      .addCase(declineTransaction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Bulk decline transactions
      .addCase(bulkDeclineTransactions.pending, (state) => {
        state.bulkLoading = true;
      })
      .addCase(bulkDeclineTransactions.fulfilled, (state, action) => {
        state.bulkLoading = false;
        // Remove declined transactions from list
        const declinedIds = action.payload.success;
        state.transactions = state.transactions.filter(t => !declinedIds.includes(t.id));
        state.selectedTransactions = [];
      })
      .addCase(bulkDeclineTransactions.rejected, (state, action) => {
        state.bulkLoading = false;
        state.error = action.payload;
      })

      // Fetch stats
      .addCase(fetchTransactionStats.fulfilled, (state, action) => {
        state.stats = action.payload;
      });
  }
});

export const {
  setFilters,
  clearFilters,
  setPage,
  setApprovedPage,
  toggleTransactionSelection,
  selectAllTransactions,
  clearSelectedTransactions,
  clearCurrentTransaction,
  clearError
} = transactionSlice.actions;

export default transactionSlice.reducer;
